import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { BookOpen, Handshake, FileText, File, DownloadSimple, SignIn } from '@phosphor-icons/react';
import { MarkdownPreview } from '../components/MarkdownPreview';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  guide: { label: 'Guide pratique', Icon: BookOpen, bg: 'bg-[#00AEEF]/10', color: 'text-[#005f8a]' },
  charte: { label: 'Charte', Icon: Handshake, bg: 'bg-[#80C51F]/10', color: 'text-[#2D5A4C]' },
  modele: { label: 'Modèle administratif', Icon: FileText, bg: 'bg-[#FF8E00]/10', color: 'text-[#b56600]' },
  autre: { label: 'Autre', Icon: File, bg: 'bg-gray-100', color: 'text-gray-700' },
};

export default function PublicLibraryPage() {
  const [templates, setTemplates] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('resources');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/public/mission-templates`).then((r) => r.data.items || []).catch(() => []),
      axios.get(`${API}/api/portal/resources`).then((r) => r.data.items || []).catch(() => []),
    ]).then(([tpls, res]) => {
      setTemplates(tpls);
      setResources(res);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F5] py-10 px-4" data-testid="public-library-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="text-center">
          <BookOpen size={44} className="text-[#2D5A4C] mx-auto" weight="duotone" />
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#1A2421] mt-2" style={{ fontFamily: 'Manrope' }}>
            Bibliothèque O'SCOP
          </h1>
          <p className="text-sm text-[#5E6D66] mt-2 max-w-2xl mx-auto">
            Guides pratiques, chartes, modèles administratifs et exemples de missions partagés par la coopérative.
          </p>
        </div>

        <div className="flex justify-center gap-2 border-b border-[#E8E6E1]">
          <button
            onClick={() => setActiveTab('resources')}
            className={`px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'resources' ? 'text-[#2D5A4C] border-b-2 border-[#2D5A4C]' : 'text-[#5E6D66] hover:text-[#1A2421]'}`}
            data-testid="tab-resources"
          >
            📚 Ressources ({resources.length})
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'templates' ? 'text-[#2D5A4C] border-b-2 border-[#2D5A4C]' : 'text-[#5E6D66] hover:text-[#1A2421]'}`}
            data-testid="tab-templates"
          >
            🌱 Modèles de missions ({templates.length})
          </button>
        </div>

        {loading && <p className="text-center text-[#5E6D66]">Chargement…</p>}

        {!loading && activeTab === 'resources' && (
          <>
            {resources.length === 0 ? (
              <Card className="border-[#E8E6E1]"><CardContent className="p-8 text-center text-[#5E6D66]">
                <Handshake size={32} className="mx-auto mb-2" weight="duotone" />
                Aucune ressource publique pour le moment.
              </CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {resources.map((r) => {
                  const meta = CATEGORY_META[r.category] || CATEGORY_META.autre;
                  const Icon = meta.Icon;
                  return (
                    <Card key={r.id} className="border-[#E8E6E1] hover:shadow-md transition-shadow" data-testid={`resource-pub-${r.id}`}>
                      <CardContent className="p-5 space-y-2">
                        <div className="flex items-start gap-2">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${meta.bg} ${meta.color} shrink-0`}>
                            <Icon size={20} weight="duotone" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-lg">{r.icon || '📄'}</span>
                              <h2 className="text-base font-bold text-[#1A2421] truncate">{r.title}</h2>
                            </div>
                            <span className={`inline-block mt-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-semibold ${meta.bg} ${meta.color}`}>{meta.label}</span>
                          </div>
                        </div>
                        {r.description && (
                          <p className="text-xs text-[#5E6D66] italic">{r.description}</p>
                        )}
                        <div className="pt-2 border-t border-[#E8E6E1]">
                          <MarkdownPreview text={r.content} className="text-[13px]" />
                        </div>
                        {r.download_url && (
                          <div className="pt-2">
                            <a
                              href={r.download_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs bg-[#2D5A4C] text-white px-3 py-1.5 rounded-md hover:bg-[#1E4136] transition-colors"
                              data-testid={`resource-download-${r.id}`}
                            >
                              <DownloadSimple size={12} weight="bold" /> Télécharger
                            </a>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {!loading && activeTab === 'templates' && (
          <>
            {templates.length === 0 ? (
              <Card className="border-[#E8E6E1]"><CardContent className="p-8 text-center text-[#5E6D66]">
                <Handshake size={32} className="mx-auto mb-2" weight="duotone" />
                Aucun modèle public pour le moment.
              </CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((t) => (
                  <Card key={t.id} className="border-[#E8E6E1]" data-testid={`public-tpl-${t.id}`}>
                    <CardContent className="p-5 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{t.icon || '📄'}</span>
                        <h2 className="text-lg font-bold text-[#1A2421]">{t.label}</h2>
                      </div>
                      <p className="text-xs uppercase tracking-wider text-[#5E6D66] font-semibold">Objet</p>
                      <p className="text-sm text-[#1A2421]">{t.objet}</p>
                      <div className="pt-2 border-t border-[#E8E6E1]">
                        <MarkdownPreview text={t.resume} className="text-[13px]" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <footer className="border-t border-[#E8E6E1] mt-10 py-6 bg-white">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5E6D66]">
          <p>&copy; {new Date().getFullYear()} Objectif SCOP Outremer</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 font-semibold text-[#2D5A4C] hover:text-[#1E4136] transition-colors"
            data-testid="public-login-link"
          >
            <SignIn size={14} weight="bold" /> Espace coopérateur — Se connecter
          </Link>
        </div>
      </footer>
    </div>
  );
}
